import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, Shield, Mail, Ticket, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getConcert, initCheckout, formatPrice, formatDateFr } from "@/lib/api";

export default function ConcertDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [concert, setConcert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedCat, setSelectedCat] = useState("");
  const [quantity, setQuantity] = useState(2);
  const [form, setForm] = useState({ full_name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getConcert(slug);
        setConcert(data);
        if (data.dates?.length) setSelectedDate(data.dates[0]);
        if (data.categories?.length) setSelectedCat(data.categories[0].name);
      } catch {
        toast.error("Concert introuvable");
        navigate("/");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, navigate]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="pt-32 max-w-7xl mx-auto px-6">
          <div className="aspect-[16/7] bg-card border border-white/10 animate-pulse rounded-sm" />
        </div>
      </>
    );
  }

  if (!concert) return null;

  const onSale = concert.status === "on_sale";
  const cat = concert.categories?.find((c) => c.name === selectedCat);
  const total = cat ? cat.price * quantity : 0;
  const isPremium = cat?.name === "Carré Or";

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!onSale) return;
    if (!selectedDate || !selectedCat) {
      toast.error("Sélectionnez une date et une catégorie.");
      return;
    }
    setSubmitting(true);
    try {
      const { checkout_url } = await initCheckout({
        concert_slug: concert.slug,
        category_name: selectedCat,
        quantity,
        email: form.email,
        full_name: form.full_name,
        selected_date: selectedDate,
      });
      // Redirect to Whop hosted checkout
      window.location.href = checkout_url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Impossible d'initier le paiement.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />

      {/* HERO */}
      <section className="relative pt-16">
        <div className="relative h-[55vh] md:h-[70vh] overflow-hidden grain">
          <img src={concert.image_url} alt={concert.artist} className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-x-0 bottom-0 max-w-7xl mx-auto px-6 lg:px-10 pb-12">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-primary mb-6" data-testid="back-link">
              <ArrowLeft size={14} /> Tous les concerts
            </Link>
            <p className="overline">
              {concert.genre.toUpperCase()} · {concert.venue}
            </p>
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] mt-3" data-testid="concert-title">
              {concert.artist}
            </h1>
            <p className="font-mono text-sm text-white/80 mt-3">{concert.title}</p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-12">
        {/* LEFT — info */}
        <div className="lg:col-span-7">
          <p className="overline">À propos du spectacle</p>
          <p className="text-lg md:text-xl text-foreground/90 leading-relaxed mt-4" data-testid="concert-description">
            {concert.description}
          </p>

          <div className="mt-10 grid grid-cols-2 gap-4">
            <div className="border border-white/10 p-5 rounded-sm">
              <div className="flex items-center gap-2 text-primary">
                <MapPin size={14} />
                <span className="font-mono text-xs tracking-[0.2em] uppercase">Lieu</span>
              </div>
              <p className="mt-2 font-bold">{concert.venue}</p>
              <p className="font-mono text-xs text-muted-foreground">{concert.city}</p>
            </div>
            <div className="border border-white/10 p-5 rounded-sm">
              <div className="flex items-center gap-2 text-primary">
                <Calendar size={14} />
                <span className="font-mono text-xs tracking-[0.2em] uppercase">Dates</span>
              </div>
              <p className="mt-2 font-bold">{concert.dates.length} représentation{concert.dates.length > 1 ? "s" : ""}</p>
              <p className="font-mono text-xs text-muted-foreground">{formatDateFr(concert.dates[0])} → {formatDateFr(concert.dates[concert.dates.length - 1])}</p>
            </div>
          </div>

          <div className="mt-10">
            <p className="overline">Toutes les dates</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {concert.dates.map((d) => {
                const active = d === selectedDate;
                return (
                  <button
                    key={d}
                    onClick={() => onSale && setSelectedDate(d)}
                    disabled={!onSale}
                    className={`font-mono text-xs px-3 py-2 rounded-sm border transition ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-white/15 text-muted-foreground hover:text-foreground hover:border-white/40"
                    } disabled:cursor-not-allowed`}
                    data-testid={`date-pill-${d}`}
                  >
                    {formatDateFr(d)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-12 grid sm:grid-cols-3 gap-4">
            <div className="border border-white/10 p-4 rounded-sm flex items-start gap-3">
              <Shield size={18} className="text-primary mt-0.5" />
              <div>
                <p className="font-bold text-sm">Paiement sécurisé</p>
                <p className="text-xs text-muted-foreground mt-1">Traité par Whop</p>
              </div>
            </div>
            <div className="border border-white/10 p-4 rounded-sm flex items-start gap-3">
              <Ticket size={18} className="text-primary mt-0.5" />
              <div>
                <p className="font-bold text-sm">E-tickets</p>
                <p className="text-xs text-muted-foreground mt-1">Reçus par e-mail</p>
              </div>
            </div>
            <div className="border border-white/10 p-4 rounded-sm flex items-start gap-3">
              <Mail size={18} className="text-primary mt-0.5" />
              <div>
                <p className="font-bold text-sm">Support 7j/7</p>
                <p className="text-xs text-muted-foreground mt-1">Réponse en 24h</p>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT — ticket selector */}
        <aside className="lg:col-span-5">
          <div className={`sticky top-24 ${isPremium && onSale ? "tracing-border" : "border border-white/10 bg-card"} rounded-sm p-6 md:p-8`}>
            <div className="flex items-center justify-between">
              <p className="overline">Billetterie</p>
              {onSale ? (
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase bg-primary text-primary-foreground px-2 py-1 rounded-sm">En vente</span>
              ) : (
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase border border-white/30 px-2 py-1 rounded-sm">Bientôt</span>
              )}
            </div>

            {!onSale ? (
              <div className="mt-6">
                <h3 className="text-2xl font-black tracking-tight">
                  Billets bientôt disponibles
                </h3>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                  La billetterie pour ce concert ouvrira prochainement. Inscrivez-vous à notre newsletter
                  pour être prévenu en avant-première et accéder à la prévente.
                </p>
                <a
                  href="/#concerts"
                  className="inline-flex w-full justify-center items-center gap-2 mt-6 border border-white/20 px-5 py-3.5 rounded-sm font-bold text-sm hover:bg-white hover:text-black transition"
                  data-testid="back-to-concerts-btn"
                >
                  Voir tous les concerts en vente
                </a>
              </div>
            ) : (
              <form onSubmit={handleCheckout} className="mt-6 space-y-5" data-testid="checkout-form">
                {/* Categories */}
                <div>
                  <label className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">Catégorie</label>
                  <div className="mt-3 grid grid-cols-1 gap-2">
                    {concert.categories.map((c) => {
                      const active = c.name === selectedCat;
                      const gold = c.name === "Carré Or";
                      return (
                        <button
                          key={c.name}
                          type="button"
                          onClick={() => setSelectedCat(c.name)}
                          className={`flex items-center justify-between px-4 py-3 rounded-sm border text-left transition ${
                            active
                              ? gold
                                ? "border-primary bg-primary/10 text-foreground"
                                : "border-white/60 bg-white/5"
                              : "border-white/10 hover:border-white/30"
                          }`}
                          data-testid={`category-${c.name.replace(/\s+/g, "-").toLowerCase()}-btn`}
                        >
                          <span className="flex items-center gap-3">
                            {gold && <span className="text-primary">★</span>}
                            <span className="font-bold text-sm">{c.name}</span>
                          </span>
                          <span className="font-mono text-sm">{formatPrice(c.price)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Quantity */}
                <div>
                  <label className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">Quantité</label>
                  <div className="mt-3 flex items-center border border-white/15 rounded-sm w-fit">
                    <button
                      type="button"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-3 hover:text-primary"
                      data-testid="qty-minus-btn"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="px-6 font-bold font-mono" data-testid="qty-value">{quantity}</span>
                    <button
                      type="button"
                      onClick={() => setQuantity(Math.min(10, quantity + 1))}
                      className="p-3 hover:text-primary"
                      data-testid="qty-plus-btn"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>

                {/* Buyer info */}
                <div className="grid gap-3">
                  <div>
                    <label className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">Nom complet</label>
                    <input
                      type="text"
                      required
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      className="mt-2 w-full bg-input border border-white/10 rounded-sm px-4 py-3 text-sm focus:border-primary outline-none"
                      placeholder="Jean Dupont"
                      data-testid="buyer-name-input"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">E-mail</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-2 w-full bg-input border border-white/10 rounded-sm px-4 py-3 text-sm focus:border-primary outline-none"
                      placeholder="vous@exemple.com"
                      data-testid="buyer-email-input"
                    />
                  </div>
                </div>

                {/* Totals */}
                <div className="border-t border-white/10 pt-5 mt-2 space-y-2">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{cat?.name} × {quantity}</span>
                    <span className="font-mono">{formatPrice(total)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>Date</span>
                    <span className="font-mono">{formatDateFr(selectedDate)}</span>
                  </div>
                  <div className="flex items-end justify-between pt-3">
                    <span className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">Total</span>
                    <span className="text-3xl font-black font-mono text-primary">{formatPrice(total)}</span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-bold text-base py-4 rounded-sm hover:brightness-110 disabled:opacity-50 transition"
                  data-testid="buy-ticket-btn"
                >
                  {submitting ? "Redirection vers Whop…" : "Acheter via Whop"}
                </button>
                <p className="text-[11px] text-muted-foreground text-center font-mono">
                  Paiement sécurisé via Whop · E-ticket immédiat
                </p>
              </form>
            )}
          </div>
        </aside>
      </section>

      <Footer />
    </>
  );
}
